import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { StanFilterConfig, StanFilterValue } from '../types';
import { DATE_FORMATS } from '@/lib';
import { DATE_RANGE_PRESETS, type DateRangePreset, findMatchingPreset } from '@/lib/utils/dateRange';

type UseDateFilterArgs = {
    filterItem: Pick<StanFilterConfig, 'id' | 'initFilterValue' | 'customDatePresets'>;
    onApply: (filters: StanFilterValue[]) => void;
    onClearFilter?: (id: string) => void;
    disabled?: boolean;
    closeMenu: () => void;
};

type ViewMode = 'presets' | 'calendar';

// Use global DateRangePreset type

export const useDateFilter = ({
    filterItem,
    onApply,
    onClearFilter,
    disabled = false,
    closeMenu,
}: UseDateFilterArgs) => {
    const [from, setFrom] = useState<Date | undefined>();
    const [to, setTo] = useState<Date | undefined>();
    const [appliedFrom, setAppliedFrom] = useState<Date | undefined>();
    const [appliedTo, setAppliedTo] = useState<Date | undefined>();
    const [viewMode, setViewMode] = useState<ViewMode>('presets');

    const summary = useMemo(() => {
        if (!appliedFrom && !appliedTo) {
            return '';
        }

        if (!appliedFrom || !appliedTo) {
            return '';
        }

        return `${format(appliedFrom, DATE_FORMATS.DISPLAY_SHORT)} - ${format(appliedTo, DATE_FORMATS.DISPLAY_SHORT)}`;
    }, [appliedFrom, appliedTo]);

    const resetState = useCallback(() => {
        setFrom(undefined);
        setTo(undefined);
        setAppliedFrom(undefined);
        setAppliedTo(undefined);
    }, []);

    const clearFilters = useCallback(
        (event?: MouseEvent) => {
            event?.stopPropagation();
            resetState();
            onApply([]);
            onClearFilter?.(filterItem.id);
            closeMenu();
        },
        [closeMenu, filterItem.id, onApply, onClearFilter, resetState]
    );

    // Initialize date state from filterItem.initFilterValue
    useEffect(() => {
        const init = filterItem.initFilterValue;
        if (init === null) {
            resetState();
            closeMenu();
        } else if (init) {
            const parseDate = (value?: string | number) => {
                if (!value) return undefined;
                const parsed = parseISO(String(value));
                return isValid(parsed) ? parsed : undefined;
            };

            const initialFrom = parseDate(init.value);
            const initialTo = parseDate(init.secondValue);

            setFrom(initialFrom);
            setTo(initialTo);
            setAppliedFrom(initialFrom);
            setAppliedTo(initialTo);
        }
    }, [filterItem.initFilterValue, resetState, closeMenu]);

    const apply = useCallback(() => {
        const out: StanFilterValue[] = [];
        if (from) {
            out.push({
                id: filterItem.id,
                operator: 'gte',
                value: format(from, DATE_FORMATS.ISO_8601),
                type: 'date',
            });
        }
        if (to) {
            out.push({
                id: filterItem.id,
                operator: 'lte',
                value: format(to, DATE_FORMATS.ISO_8601),
                type: 'date',
            });
        }
        onApply(out);
        setAppliedFrom(from);
        setAppliedTo(to);
        setViewMode('presets'); // Reset to presets view after applying
        closeMenu();
    }, [closeMenu, filterItem.id, from, onApply, to, setViewMode]);

    /**
     * Uses custom date range presets if provided, otherwise falls back to global presets
     */
    const presetOptions: DateRangePreset[] = filterItem.customDatePresets || DATE_RANGE_PRESETS;

    /**
     * Detects if current selection matches any preset using global utility
     */
    const getSelectedPreset = useCallback(() => {
        if (!from || !to) return null;
        return findMatchingPreset(from, to, presetOptions);
    }, [from, to, presetOptions]);

    /**
     * Checks if we have a custom date range (not matching any preset)
     */
    const isCustomRange = from && to && !getSelectedPreset();

    /**
     * Formats the selected date range for display
     */
    const selectedRangeText =
        !from || !to ? '' : `${format(from, DATE_FORMATS.DISPLAY_SHORT)} - ${format(to, DATE_FORMATS.DISPLAY_SHORT)}`;

    /**
     * Handles preset selection and immediately applies the filter
     */
    const handlePresetSelect = useCallback(
        (preset: DateRangePreset) => {
            if (disabled) return;

            const { from: presetFrom, to: presetTo } = preset.getDateRange();
            setFrom(presetFrom);
            setTo(presetTo);

            // Directly apply the preset selection
            const out: StanFilterValue[] = [];
            if (presetFrom) {
                out.push({
                    id: filterItem.id,
                    operator: 'gte',
                    value: format(presetFrom, DATE_FORMATS.ISO_8601),
                    type: 'date',
                });
            }
            if (presetTo) {
                out.push({
                    id: filterItem.id,
                    operator: 'lte',
                    value: format(presetTo, DATE_FORMATS.ISO_8601),
                    type: 'date',
                });
            }
            onApply(out);
            closeMenu();
        },
        [disabled, filterItem.id, onApply, setFrom, setTo, closeMenu]
    );

    /**
     * Switches to calendar view for custom date selection
     */
    const handleAbsoluteDatesClick = () => {
        setViewMode('calendar');
    };

    /**
     * Returns to preset view
     */
    const handleBackToPresets = () => {
        setViewMode('presets');
    };

    /**
     * Handles date range selection from calendar
     */
    const handleRangeSelect = useCallback(
        (range: DateRange | undefined) => {
            if (disabled) return;
            setFrom(range?.from);
            setTo(range?.to);
        },
        [disabled, setFrom, setTo]
    );

    const hasSummary = summary.length > 0;

    return {
        summary,
        hasSummary,
        from,
        setFrom,
        to,
        setTo,
        apply,
        clearFilters,
        setAppliedFrom,
        setAppliedTo,
        // New date-specific functionality
        viewMode,
        presetOptions,
        getSelectedPreset,
        isCustomRange,
        selectedRangeText,
        handlePresetSelect,
        handleAbsoluteDatesClick,
        handleBackToPresets,
        handleRangeSelect,
    };
};
